import * as THREE from 'three';

/**
 * Constants for cube dimensions and positions
 * @constant {Object}
 */
const CUBE_CONSTANTS = {
    SIZE: 3,
    PIECE_OFFSETS: {
        CORNER: 2,
        EDGE: 1,
        CENTER: 0
    },
    FACE_INDICES: {
        U: 0,
        R: 1,
        F: 2,
        D: 3,
        L: 4,
        B: 5
    }
};

/**
 * Vector class for 3D coordinates with improved methods
 * @class
 */
class Vec3 {
    constructor(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
    }

    copy() {
        return new Vec3(this.x, this.y, this.z);
    }

    applyAxisAngle(axis, angle) {
        // Rodrigues rotation formula
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dot = this.dot(axis);
        
        const x = this.x * cos + (axis.y * this.z - axis.z * this.y) * sin + axis.x * dot * (1 - cos);
        const y = this.y * cos + (axis.z * this.x - axis.x * this.z) * sin + axis.y * dot * (1 - cos);
        const z = this.z * cos + (axis.x * this.y - axis.y * this.x) * sin + axis.z * dot * (1 - cos);
        
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    }

    dot(other) {
        return this.x * other.x + this.y * other.y + this.z * other.z;
    }

    round() {
        this.x = Math.round(this.x);
        this.y = Math.round(this.y);
        this.z = Math.round(this.z);
        return this;
    }

    equals(other) {
        return this.x === other.x && this.y === other.y && this.z === other.z;
    }

    toString() {
        return `(${this.x}, ${this.y}, ${this.z})`;
    }
}

/**
 * Sticker class representing a facelet on the cube
 * @class
 */
class Sticker {
    constructor(pos, dst = null) {
        this.pos = pos;
        this.dst = dst || pos.copy();
    }

    copy() {
        return new Sticker(this.pos.copy(), this.dst.copy());
    }
}

/**
 * Move class representing a cube rotation
 * @class
 */
class Move {
    constructor(name, axis, angle, predicate) {
        this.name = name;
        this.axis = new Vec3(axis.x, axis.y, axis.z);
        this.angle = angle * Math.PI / 180; // Convert to radians
        this.predicate = predicate;
    }

    apply(sticker) {
        if (this.predicate(sticker.pos)) {
            const newPos = sticker.pos.copy().applyAxisAngle(this.axis, -this.angle).round();
            const newDst = sticker.dst.copy().applyAxisAngle(this.axis, -this.angle).round();
            return new Sticker(newPos, newDst);
        }
        return sticker;
    }
}

/**
 * Factory class for creating cube moves
 * @class
 */
class MoveFactory {
    static createMoveSet(baseName, axis, predicate) {
        return [
            new Move(baseName, axis, 90, predicate),
            new Move(baseName + "2", axis, 180, predicate),
            new Move(baseName + "'", axis, 270, predicate)
        ];
    }

    static createAxisMoves() {
        const moves = {};
        
        // Outer layer moves
        const U = this.createMoveSet("U", new Vec3(0, 1, 0), pos => pos.y > 0);
        const D = this.createMoveSet("D", new Vec3(0, 1, 0), pos => pos.y < 0);
        const L = this.createMoveSet("L", new Vec3(-1, 0, 0), pos => pos.x < 0);
        const R = this.createMoveSet("R", new Vec3(1, 0, 0), pos => pos.x > 0);
        const F = this.createMoveSet("F", new Vec3(0, 0, 1), pos => pos.z > 0);
        const B = this.createMoveSet("B", new Vec3(0, 0, -1), pos => pos.z < 0);

        // Inner layer moves
        const u = this.createMoveSet("u", new Vec3(0, 1, 0), pos => pos.y >= 0);
        const d = this.createMoveSet("d", new Vec3(0, 1, 0), pos => pos.y <= 0);
        const l = this.createMoveSet("l", new Vec3(-1, 0, 0), pos => pos.x <= 0);
        const r = this.createMoveSet("r", new Vec3(1, 0, 0), pos => pos.x >= 0);
        const f = this.createMoveSet("f", new Vec3(0, 0, 1), pos => pos.z >= 0);
        const b = this.createMoveSet("b", new Vec3(0, 0, -1), pos => pos.z <= 0);

        // Slice moves
        const E = this.createMoveSet("E", new Vec3(0, 1, 0), pos => pos.y === 0);
        const M = this.createMoveSet("M", new Vec3(-1, 0, 0), pos => pos.x === 0);
        const S = this.createMoveSet("S", new Vec3(0, 0, 1), pos => pos.z === 0);

        // Whole cube rotations
        const x = this.createMoveSet("x", new Vec3(1, 0, 0), () => true);
        const y = this.createMoveSet("y", new Vec3(0, 1, 0), () => true);
        const z = this.createMoveSet("z", new Vec3(0, 0, 1), () => true);

        // Add all moves to the moves object
        [U, D, L, R, F, B, u, d, l, r, f, b, E, M, S, x, y, z]
            .flat()
            .forEach(move => moves[move.name] = move);

        return moves;
    }
}

/**
 * Factory class for creating cube states
 * @class
 */
class CubeFactory {
    /**
     * Create stickers for a piece at the given center position with given offsets
     * @param {Vec3} center - The center position of the piece
     * @param {Vec3[]} offsets - Array of offset vectors for each sticker
     * @param {Sticker[]} cube - The cube array to add stickers to
     */
    static createPieceStickers(center, offsets, cube) {
        offsets.forEach(offset => {
            const pos = new Vec3(
                center.x + offset.x,
                center.y + offset.y,
                center.z + offset.z
            );
            cube.push(new Sticker(pos));
        });
    }

    /**
     * Create a solved cube state
     * @returns {Sticker[]} Array of stickers representing a solved cube
     */
    static createSolvedCube() {
        const cube = [];
        
        // Create corners
        CubeFactory.createPieceStickers(new Vec3(2, 2, 2), [
            new Vec3(0, 1, 0), new Vec3(1, 0, 0), new Vec3(0, 0, 1)  // URF
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, 2, 2), [
            new Vec3(0, 1, 0), new Vec3(-1, 0, 0), new Vec3(0, 0, 1)  // ULF
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, 2, -2), [
            new Vec3(0, 1, 0), new Vec3(1, 0, 0), new Vec3(0, 0, -1)  // URB
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, 2, -2), [
            new Vec3(0, 1, 0), new Vec3(-1, 0, 0), new Vec3(0, 0, -1)  // ULB
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, -2, 2), [
            new Vec3(0, -1, 0), new Vec3(1, 0, 0), new Vec3(0, 0, 1)  // DRF
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, -2, 2), [
            new Vec3(0, -1, 0), new Vec3(-1, 0, 0), new Vec3(0, 0, 1)  // DLF
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, -2, -2), [
            new Vec3(0, -1, 0), new Vec3(1, 0, 0), new Vec3(0, 0, -1)  // DRB
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, -2, -2), [
            new Vec3(0, -1, 0), new Vec3(-1, 0, 0), new Vec3(0, 0, -1)  // DLB
        ], cube);

        // Create edges
        CubeFactory.createPieceStickers(new Vec3(0, 2, 2), [
            new Vec3(0, 1, 0), new Vec3(0, 0, 1)  // UF
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, 2, 0), [
            new Vec3(0, 1, 0), new Vec3(1, 0, 0)  // UR
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(0, 2, -2), [
            new Vec3(0, 1, 0), new Vec3(0, 0, -1)  // UB
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, 2, 0), [
            new Vec3(0, 1, 0), new Vec3(-1, 0, 0)  // UL
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, 0, 2), [
            new Vec3(1, 0, 0), new Vec3(0, 0, 1)  // FR
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, 0, 2), [
            new Vec3(-1, 0, 0), new Vec3(0, 0, 1)  // FL
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, 0, -2), [
            new Vec3(1, 0, 0), new Vec3(0, 0, -1)  // BR
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, 0, -2), [
            new Vec3(-1, 0, 0), new Vec3(0, 0, -1)  // BL
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(0, -2, 2), [
            new Vec3(0, -1, 0), new Vec3(0, 0, 1)  // DF
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, -2, 0), [
            new Vec3(0, -1, 0), new Vec3(1, 0, 0)  // DR
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(0, -2, -2), [
            new Vec3(0, -1, 0), new Vec3(0, 0, -1)  // DB
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, -2, 0), [
            new Vec3(0, -1, 0), new Vec3(-1, 0, 0)  // DL
        ], cube);

        // Create centers
        CubeFactory.createPieceStickers(new Vec3(0, 2, 0), [
            new Vec3(0, 1, 0)  // U
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(0, -2, 0), [
            new Vec3(0, -1, 0)  // D
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(2, 0, 0), [
            new Vec3(1, 0, 0)  // R
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(-2, 0, 0), [
            new Vec3(-1, 0, 0)  // L
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(0, 0, 2), [
            new Vec3(0, 0, 1)  // F
        ], cube);
        CubeFactory.createPieceStickers(new Vec3(0, 0, -2), [
            new Vec3(0, 0, -1)  // B
        ], cube);

        return cube;
    }
}

/**
 * Get the face a sticker belongs to based on its position
 * @param {Sticker} sticker - The sticker to check
 * @returns {string|null} The face name or null if not on a face
 */
function getFace(sticker) {
    const { x, y, z } = sticker.dst;
    if (y === CUBE_CONSTANTS.SIZE) return 'U';
    if (y === -CUBE_CONSTANTS.SIZE) return 'D';
    if (x === CUBE_CONSTANTS.SIZE) return 'R';
    if (x === -CUBE_CONSTANTS.SIZE) return 'L';
    if (z === CUBE_CONSTANTS.SIZE) return 'F';
    if (z === -CUBE_CONSTANTS.SIZE) return 'B';
    return null;
}

/**
 * Apply a move to the cube
 * @param {Sticker[]} cube - The current cube state
 * @param {Move} move - The move to apply
 * @returns {Sticker[]} The new cube state
 */
function applyGmove(cube, move) {
    return cube.map(sticker => move.apply(sticker));
}

/**
 * Apply a sequence of moves to the cube
 * @param {Sticker[]} cube - The current cube state
 * @param {string} moves - Space-separated list of moves
 * @returns {Sticker[]} The new cube state
 */
function applyGmoves(cube, moves) {
    return moves.trim().split(/\s+/).filter(s => s).map(m => MOVES[m]).reduce(applyGmove, cube);
}

// Create moves object
const MOVES = MoveFactory.createAxisMoves();

export {
    Vec3,
    Sticker,
    Move,
    MoveFactory,
    CubeFactory,
    getFace,
    applyGmove,
    applyGmoves,
    MOVES,
    CUBE_CONSTANTS
};

export const createSolvedCube = CubeFactory.createSolvedCube; 